/**
 * Credit Display Component
 * 
 * Shows user's remaining credits in a compact badge
 * Click to view details or top up
 */

import { Sparkles, Zap } from 'lucide-react';
import { useCredits } from '@/hooks/useCredits';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function CreditDisplay() {
  const creditStatus = useCredits();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Don't show for anonymous users
  if (!user) {
    return null;
  }

  // Don't show while loading to prevent flash of "3"
  if (creditStatus.isChecking) {
    return null;
  }

  // Don't show for admin/pro with unlimited
  if (creditStatus.triesRemaining === 'unlimited') {
    return null;
  }

  const isLow = typeof creditStatus.triesRemaining === 'number' && creditStatus.triesRemaining <= 3;
  const isCritical = typeof creditStatus.triesRemaining === 'number' && creditStatus.triesRemaining === 0;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => navigate('/billing')}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold
              transition-all hover:scale-105 hover:shadow-md
              ${isCritical 
                ? 'bg-red-100 text-red-700 border-2 border-red-300 animate-pulse' 
                : isLow
                ? 'bg-amber-100 text-amber-700 border-2 border-amber-300'
                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }
            `}
          >
            <Zap className={`h-3.5 w-3.5 ${isCritical ? 'text-red-600' : isLow ? 'text-amber-600' : ''}`} />
            <span>{creditStatus.triesRemaining}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-center">
            <p className="font-semibold mb-1">
              {isCritical 
                ? 'Out of try-on credits!' 
                : isLow 
                ? 'Running low on credits' 
                : 'Try-on credits remaining'
              }
            </p>
            <p className="text-xs text-gray-400">
              {isCritical 
                ? 'Click to upgrade or buy more' 
                : 'Click to view details or top up'
              }
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Compact inline version for tight spaces
 */
export function CreditBadge() {
  const creditStatus = useCredits();
  const { user } = useAuth();

  // Don't show while loading to prevent flash of "3"
  if (!user || creditStatus.isChecking || creditStatus.triesRemaining === 'unlimited') {
    return null;
  }

  const isLow = typeof creditStatus.triesRemaining === 'number' && creditStatus.triesRemaining <= 3;

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
        ${isLow ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}
      `}
    >
      <Zap className="h-3 w-3" />
      {creditStatus.triesRemaining}
    </span>
  );
}


